import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { http, errMsg, API } from "../../lib/api";
import { PageImg } from "./PageImg";

const STAGES = [["building", "Building"], ["stopped", "Stopped"], ["page_check", "Page check"], ["style_check", "Style check"], ["approve", "Ready to approve"], ["live", "Live"]];

const Card = ({ k, picked, toggle, review }) => (
  <div data-testid={`pipe-card-${k.id}`} className="rounded-md border border-stone-200 bg-white p-2.5 text-sm">
    <div className="flex items-start gap-2">
      {k.stage === "approve" && <input data-testid={`pipe-pick-${k.id}`} type="checkbox" checked={picked} onChange={() => toggle(k.id)} className="mt-1" />}
      <Link to={`/kits/${k.id}`} className="min-w-0 flex-1 font-display leading-tight text-[#2B2A26] hover:text-[#9A7B2C]">{k.destination}</Link>
    </div>
    <div className="mt-1 text-[11px] text-stone-500">{k.edition} · {k.tier}{k.batch ? " · batch" : ""}{k.collection === "founders" ? " · Founder's" : ""}</div>
    {k.stage === "page_check" && k.light > 0 && <div className="mt-1 text-[11px] text-amber-700">{k.light} page(s) to fix</div>}
    {k.style?.length > 0 && <div className="mt-1 text-[11px] text-red-600">{k.style.join(" · ")}</div>}
    {k.stage === "approve" && !k.cover && <div className="mt-1 text-[11px] text-red-600">Needs a cover photo</div>}
    {["approve", "style_check", "page_check"].includes(k.stage) && <button data-testid={`pipe-review-${k.id}`} onClick={() => review(k)} className="mt-1.5 text-xs font-bold text-[#9A7B2C] hover:underline">Review</button>}
  </div>
);

const Review = ({ k, onClose, onDone }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (k) http.get(`/kits/${k.id}/style-check`).then((r) => setD(r.data)).catch(() => {}); }, [k]);
  const approve = async () => {
    setBusy(true);
    try { const { data } = await http.get(`/kits/${k.id}`); await http.put(`/kits/${k.id}/product`, { ...data.product, published: true }); toast.success(`${k.destination} is live`); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!k} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approve-dialog" className="max-w-5xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{k?.destination}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-3">
          {(k?.samples || []).map((n) => <PageImg key={n} src={`${API}/kits/${k.id}/pdf/page/${n}?v=${encodeURIComponent(k.updated_at || "")}`} className="aspect-[210/297] w-full border border-stone-200 shadow-sm" />)}
        </div>
        {d && (d.issues.length ? <ul className="mt-3 text-sm">{d.issues.map((i) => <li key={i.key} className="border-t border-stone-100 py-1"><b>{i.label}</b> <span className="text-stone-600">{i.detail}</span></li>)}</ul>
          : <p className="mt-3 text-sm text-emerald-700">No house-style issues.</p>)}
        <div className="mt-3 flex gap-2">
          <button data-testid="approve-publish-btn" disabled={busy || (d && !d.passed)} onClick={approve} className="inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] px-4 py-2 text-sm font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve & publish
          </button>
          <Link to={`/kits/${k?.id}`} className="rounded-sm border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50">Open full kit</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PipelineBoard = () => {
  const [kits, setKits] = useState(null);
  const [picked, setPicked] = useState([]);
  const [rev, setRev] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/ops/pipeline").then((r) => setKits(r.data.kits)).catch(() => setKits([])), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const approveAll = async () => {
    setBusy(true);
    let ok = 0;
    for (const id of picked) {
      try { const { data } = await http.get(`/kits/${id}`); await http.put(`/kits/${id}/product`, { ...data.product, published: true }); ok += 1; }
      catch (e) { toast.error(`${kits.find((k) => k.id === id)?.destination}: ${errMsg(e)}`); }
    }
    if (ok) toast.success(`${ok} kit(s) published`);
    setPicked([]); setBusy(false); load();
  };
  if (!kits) return <Loader2 className="animate-spin text-[#9A7B2C]" />;
  return (
    <div data-testid="pipeline-board">
      <div className="mb-3 flex items-center gap-3">
        <span className="text-sm text-stone-500">Every kit moves left to right: building, then the page check and the style check, then your approval, then live.</span>
        <button data-testid="pipe-approve-selected" disabled={busy || !picked.length} onClick={approveAll} className="ml-auto rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">Approve & publish selected ({picked.length})</button>
      </div>
      <div className="grid grid-cols-6 gap-3">
        {STAGES.map(([key, label]) => {
          const col = kits.filter((k) => k.stage === key);
          return (
            <div key={key} data-testid={`pipe-col-${key}`} className="rounded-lg bg-stone-50 p-2">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">{label} <span className="text-stone-400">{col.length}</span></div>
              <div className="space-y-2">{col.map((k) => <Card key={k.id} k={k} picked={picked.includes(k.id)} toggle={toggle} review={setRev} />)}</div>
            </div>
          );
        })}
      </div>
      <Review k={rev} onClose={() => setRev(null)} onDone={load} />
    </div>
  );
};
